/**
 * A mutable flag, shaped like a React ref so a hook can hold it in `useRef`.
 */
export interface InFlightFlag {
  current: boolean;
}

/**
 * @name runIfIdle
 * @description
 * Runs `task` only when no earlier call holding the same `flag` is still in
 * flight; a call made while one is running resolves to `undefined` without
 * starting `task`. The flag is set synchronously, before the first `await`,
 * so two submits dispatched in the same tick cannot both get through.
 *
 * Why this exists (BALLEE-K3): the sign-up flow awaits a pre-signup identity
 * check before it calls GoTrue. Its old guard read `mutation.isPending`, which
 * stays false during that await, so a double tap sent two `/signup` requests
 * for one email. The second insert blocked on `users_email_partial_key` until
 * the first committed, then failed, and GoTrue answered 500 "Database error
 * saving new user".
 */
export async function runIfIdle<T>(
  flag: InFlightFlag,
  task: () => Promise<T>,
): Promise<T | undefined> {
  if (flag.current) {
    return undefined;
  }

  flag.current = true;

  try {
    return await task();
  } finally {
    flag.current = false;
  }
}
